/**
 * usePasswordResetCompletion — the reset flow's last step: judge the new password, send it, and say
 * what a refusal means. Composed by `usePasswordResetFlow`, which owns the state machine.
 *
 * The PASSWORD is judged by the ONE ecosystem policy, `@puffin-core/password-policy` (NIST SP
 * 800-63B-4, owner ruling on L-R7CLOSE row 39): the submit gate here, the checklist the step renders
 * (`@puffin-core/auth-shell`'s `<PasswordRequirementList>`, handed `passwordContext`) and the server
 * all read it, against the same context — the address being reset and the company's subdomain. The
 * flow is pre-auth, so no names are known; the server, which loads the person, judges those too and
 * NAMES what failed, and `usePasswordRefusalText` turns that into the reader's language rather than
 * the server's English. UMB lane L16 built the flow on the older composition table; the two met at
 * row 42 and that table went.
 *
 * @module @puffin-core/login-shell/hooks/usePasswordResetCompletion
 */

import { useCallback, useMemo } from 'react';
import { usePasswordRefusalText } from '@puffin-core/auth-shell';
import { validatePassword } from '@puffin-core/password-policy';

/**
 * @param {Object} options
 * @param {Object} options.state - the flow's state (`email`, `subdomain` — the company the code was sent for —
 *   `newPassword`, `confirmPassword`, `resetToken`, `isLoading`)
 * @param {(action: Object) => void} options.dispatch - the flow's reducer dispatch
 * @param {(resetToken: string, newPassword: string) => Promise<any>} options.onComplete
 * @param {() => void} [options.onSuccess] - called once the new password is saved
 * @param {(err: Error, stage: string, text?: string|null) => void} options.fail - the flow's
 *   refusal recorder
 * @returns {{passwordContext: Object, handleCompleteReset: (e: Event) => Promise<void>}}
 */
export function usePasswordResetCompletion({
  state,
  dispatch,
  onComplete,
  onSuccess,
  fail,
}) {
  const refusalText = usePasswordRefusalText();
  // Who is resetting, as far as a pre-auth flow knows: the address and the company's subdomain.
  const passwordContext = useMemo(
    () => ({ email: state.email, words: [state.subdomain] }),
    [state.email, state.subdomain],
  );

  const handleCompleteReset = useCallback(
    async (e) => {
      e.preventDefault();
      if (state.isLoading) return;
      // The ONE policy the live checklist renders, against the same context — the gate cannot
      // disagree with what was shown.
      if (!validatePassword(state.newPassword, passwordContext).valid) {
        dispatch({
          type: 'SUBMIT_ERROR',
          error: { key: 'modal.passwordReset.error.requirementsUnmet' },
        });
        return;
      }
      if (state.newPassword !== state.confirmPassword) {
        dispatch({ type: 'SUBMIT_ERROR', error: { key: 'modal.passwordReset.error.mismatch' } });
        return;
      }
      dispatch({ type: 'SUBMIT_START' });
      try {
        await onComplete(state.resetToken, state.newPassword);
      } catch (err) {
        // A password-policy refusal NAMES what failed — the person's own names included, which
        // this pre-auth checklist cannot know. Anything else keeps the class sentence.
        fail(err, 'complete', refusalText(err));
        return;
      }
      dispatch({ type: 'PASSWORD_CHANGED' });
      // Outside the `try`: a host's own navigation failing must not read as a refused password.
      if (onSuccess) onSuccess();
    },
    [
      state.newPassword,
      state.confirmPassword,
      state.resetToken,
      state.isLoading,
      passwordContext,
      dispatch,
      onComplete,
      onSuccess,
      fail,
      refusalText,
    ],
  );

  return { passwordContext, handleCompleteReset };
}

export default usePasswordResetCompletion;
